import { useTranslate } from "@refinedev/core";
import { Flex } from "antd";
import { ChoiceCard } from "@libertify/ui";
import { useTeacherOnboarding } from "./teacherOnboardingContext";
import StepHeader from "./StepHeader";
import { I18N_PREFIX } from "./constants";
import {
  ILLUSTRATION_OPTIONS,
  STRUCTURE_OPTIONS,
  VOICE1_OPTION_KEY,
  VOICE1_OPTIONS,
  VOICE2_OPTIONS,
} from "../creator-profile/dnaOptions";
import type { TeacherAnswers, TeacherStep } from "./types";

interface ChoiceConfig {
  i18nBase: string;
  field: keyof TeacherAnswers;
  options: { code: string; optionKey: string }[];
}

const getConfig = (
  step: TeacherStep,
  voice1: TeacherAnswers["voice1"]
): ChoiceConfig | null => {
  switch (step) {
    case "voice1":
      return {
        i18nBase: "voice",
        field: "voice1",
        options: VOICE1_OPTIONS.map((code) => ({
          code,
          optionKey: VOICE1_OPTION_KEY[code],
        })),
      };
    case "voice2":
      return {
        i18nBase: "voice2",
        field: "voice2",
        options: (voice1 ? VOICE2_OPTIONS[voice1] : []).map((code) => ({
          code,
          optionKey: code,
        })),
      };
    case "illustration":
      return {
        i18nBase: "illustration",
        field: "illustration",
        options: ILLUSTRATION_OPTIONS.map((code) => ({ code, optionKey: code })),
      };
    case "structure":
      return {
        i18nBase: "structure",
        field: "structure",
        options: STRUCTURE_OPTIONS.map((code) => ({ code, optionKey: code })),
      };
    default:
      return null;
  }
};

const ChoiceStep = () => {
  const t = useTranslate();
  const { currentStep, answers, setAnswer } = useTeacherOnboarding();

  const config = getConfig(currentStep, answers.voice1);
  if (!config) return null;

  const { i18nBase, field, options } = config;
  const selected = answers[field];

  return (
    <Flex vertical align="center" gap={24} style={{ width: "100%" }}>
      <StepHeader i18nBase={i18nBase} />

      <Flex vertical gap={12} style={{ width: "100%", maxWidth: 560 }}>
        {options.map(({ code, optionKey }) => {
          const isSelected = selected === code;
          return (
            <ChoiceCard
              key={code}
              label={t(`${I18N_PREFIX}.${i18nBase}.options.${optionKey}.label`)}
              description={t(
                `${I18N_PREFIX}.${i18nBase}.options.${optionKey}.quote`
              )}
              selected={isSelected}
              badge={t(`${I18N_PREFIX}.thisone`)}
              onClick={() => setAnswer({ [field]: code })}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

export default ChoiceStep;
